import configment from '@adc/configment'
import logger from '@adc/logger'
// @ts-ignore no types
import { get, pick } from 'lodash'
// @ts-ignore no types
import newAnalytics from '@adc/analytics'
import type { primitives } from '@adc/blueprint'
import type { Listing } from '@adc/sdk/types'
import { Address } from 'common/components/address'
import { FeatureFlags } from '@adc/ui-adc'
import type { AiChatSubmitType } from '@adc/ui-adc'
import Cookies from 'js-cookie'
type User = primitives.AuctionUser

type TesteableFeatureFlags = FeatureFlags & {
  [key: string]: { test?: Record<string, any> } | undefined
}

// TODO these events Name must be under DS
// https://github.com/auctioncorp/design-system/blob/main/packages/analytics/src/common/eventNames.ts
const SHOULD_FIRE_PAGE_GA_TRACKING = false
const CHANNEL = 'resi-fe-pdp'
export const BID_DEPOSIT_EVENT_NAME = 'bid_deposit'
const PDP_SEARCH_SUBMIT_EVENT_NAME = 'pdp-search-submit'
const DUAL_TRACK_PATH_SELECTED_EVENT_NAME = 'dual-track-path-selected'
const DUAL_TRACK_MODAL_VIEWED_EVENT_NAME = 'dual-track-modal-viewed'
const DUAL_TRACK_MODAL_OUTSIDE_CLICK_EVENT_NAME =
  'dual-track-modal-outside-click'
const REO_PDP_PAGE_NAME = 'pdp|reo'
// const CONTRACT_PAGE_NAME = 'contract'

interface TrackingEvent {
  eventName?: string
  additionalFields?: Record<string, unknown>
}

interface BidDepositEvent extends TrackingEvent {
  type?: string
  event_category?: string
}

interface AnalyticsType {
  track: (event: TrackingEvent) => void
  bidDeposit: {
    track: (event: BidDepositEvent) => void
  }
  trackSearchSubmit: (user: User) => void
  popup: (
    tooltipName: string,
    additionalFields?: Record<string, unknown>
  ) => void
  documents: (doc?: { label: string; display_label?: string }) => void
  compInteraction: (compAddress?: string) => void
  property: {
    save: (listing?: Listing) => void
    saveSimilarProperty: (listing?: Listing, save?: boolean) => void
    trackSimilarPropertyClick: (
      listing: Listing,
      additionalPropertyData?: Record<string, unknown>
    ) => void
    removeSaved: (
      listing: Listing,
      additionalPropertyData?: Record<string, unknown>
    ) => void
  }
  bidBanner: {
    unauthenticatedStatusChange: () => void
    bidStatusChange: (bidStatus: string) => void
    confirmBid: {
      success: (bidAmount: number) => void
      fail: () => void
    }
    nextBidSubmit: (listing: Listing) => void
  }
  maxBid: {
    trackSetAutoBidClick: (listing: Listing) => void
    trackSetAutoBidConfirm: {
      success: (listing: Listing, maxBidAmount: number | undefined) => void
      fail: (listing: Listing) => void
    }
    trackIncreaseAutoBidClick: (listing: Listing) => void
    trackIncreaseAutoBidConfirm: {
      success: (listing: Listing, maxBidAmount: number | undefined) => void
      fail: (listing: Listing) => void
    }
    trackAdjustAutoBidClick: (listing: Listing) => void
    trackAdjustAutoBidConfirm: {
      success: (listing: Listing, maxBidAmount: number | undefined) => void
      fail: (listing: Listing) => void
    }
    trackRemoveAutoBidClick: (listing: Listing) => void
    trackRemoveAutoBidConfirm: {
      success: (listing: Listing) => void
      fail: (listing: Listing) => void
    }
  }
  purchaseTitleReport: () => void
  aiAssistantChat: {
    trackSubmitType: (chatSubmitType: AiChatSubmitType) => void
    trackConversationStart: (conversationId: string) => void
    trackAiQuestionClick: (linkPosition: number, linkName: string) => void
    trackLiveAgentBannerDisplayed: () => void
    trackLiveAgentClick: () => void
  }
  trackPDP: (params: {
    featureFlags?: FeatureFlags
    listing: Listing
    user?: User
  }) => void
  trackPrint: (data: Listing) => void
  trackMakeYourOfferClick: () => void
  firstLookModal: {
    start: () => void
    acknowledgements: (buyerCertificationType: string) => void
    back: (buyerCertificationType: string) => void
    done: (buyerCertificationType: string) => void
    complete: (buyerCertificationType: string) => void
  }
  remoteBidding: {
    qualifyRemoteBidButtonClick: (isUserLoggedIn: boolean) => void
    joinTheAuctionRemoteBidButtonClick: (isUserLoggedIn: boolean) => void
  }
  privateSeller: {
    makeAnOfferBidBoxButtonClick: () => void
    makeAnOfferStickyHeaderButtonClick: () => void
    makeAnOffer: (params: {
      intent: string
      status: string
      listing: Listing
    }) => void
  }
  dualTrack: {
    pathSelected: (params: { path: string; source: 'toggle' | 'modal' }) => void
    modalViewed: () => void
    modalOutsideClick: () => void
  }
  initialize: (
    propertyContext?: Record<string, unknown>,
    user?: User,
    featureFlags?: FeatureFlags
  ) => void
}

const analytics: AnalyticsType = {} as AnalyticsType
let everyCall: Record<string, unknown> = {}

const commonTrackingValues = (): Partial<Record<string, unknown>> => {
  return {
    ...pick(everyCall, ['morpheusListingId', 'morpheusPropertyId']),
  }
}

const ACTION_VALUES = {
  /**
   * Login/Signup
   */
  LOGIN: 'login',
  SIGNUP: 'signup',

  /**
   * Options Ribbon
   */
  PRINT: 'print',
  DIRECTIONS: 'directions',

  /**
   * PropertyGallery component
   */
  IMAGES_VIEW: 'photos',
  MAP_VIEW: 'map',
  STREET_VIEW: 'street',

  /**
   * Contacting an agent
   */
  CONTACT_AGENT: 'contact_agent|listingagent',
  OPEN_HOUSE: 'contact_agent|openhouse',

  /**
   * Chat
   */
  CHAT: 'chat',

  /**
   * Saved assets
   */
  SAVED_ASSET: 'savedasset',
  VIEW_SAVED_ASSETS: 'viewallsaved',

  /**
   * Contracting
   */
  CONTRACTING_FORM_SUBMIT: 'contracting-form-submit',
  CONTRACTING_SUBMIT_SUCCESS: 'success',
  CONTRACTING_SUBMIT_FAILURE: 'fail',

  /**
   * Purchase Profiles
   */
  PURCHASE_PROFILES_SELECT_PROFILE:
    'contracting-purchase-profiles-select-profile',
  PURCHASE_PROFILES_UPDATE_PROFILE:
    'contracting-purchase-profiles-update-profile',
  PURCHASE_PROFILES_CREATE_PROFILE:
    'contracting-purchase-profiles-save-new-profile',

  /**
   * Other
   */
  EVENT_DETAILS: 'event.details',
  REGISTRATION_START: 'registration|start',
  PROPERTY_WATCH: 'propertywatchsubmitted',
  SEARCH_AGAIN: 'search_again',

  /**
   * TPS
   */
  TERMS_LIGHTBOX: 'whataretheseterms',
  FORECLOSURE_FAQ: 'foreclosurefaq',
  PROPERTY_APPRAISER: 'contacts.appraiser',
  CLERK_COURT: 'contacts.clerkofcourt',
  CUST_SERVICE: 'contacts.CS',
  BORROWERS: 'contacts.Borrowers',
  VENUE: 'event.location',
  BID_COUNTY: 'bidatcountysite',
  BID_COUNTY_CONTINUE: 'countysitecontinue',
  BID_COUNTY_CHECKBOX: 'countysitecheck',
  WHAT_TO_BRING: 'whattobring',
  VIEW_ALL_DOCS: 'viewalldocs',
  EMAIL: 'email',
  BID_NOW_BUTTON: 'bidnow',
  BID_PLACED: 'bidplaced',
  MAX_BID_INFO_TOOLTIP: 'maxbidinfotooltip',
  EST_VALUE_RANGE_INFO_TOOLTIP: 'estvaluerangeinfotooltip',
  CLOSE_CONFIRM_LB: 'bidcloseconfirmlightbox',
  CONFIRM_LB: 'bidconfirmlightbox',
  SHOW_BID_HISTORY: 'bidhistory',
  REGISTRATION_CLOSED: 'tpsregistrationclosed',
  REGISTRATION_COMPLETED: 'tpsregistrationcompleted',
  REG_CHAT: 'tpsregistrationchat',
  REG_SOURCE: 'tpsregistration',
  PARTICIPATION_TERMS: 'tpsregistrationparticipationterms',
  PDP_SOURCE: 'pdp|trustee',

  /**
   * Make Your Offer
   */
  MAKE_YOUR_OFFER: 'make-your-offer-pdp',

  /**
   * Similar Properties
   */
  SIMILAR_PROPERTY_CLICK: 'similar-properties-click',

  /**
   * First Look Modal
   */
  FIRST_LOOK_MODAL_BACK: 'first-look-modal-back',
  FIRST_LOOK_MODAL_DONE: 'first-look-modal-done',
}

const FIRST_LOOK_MODAL = {
  START: 'first-look-modal-start',
  ACKNOWLEDGEMENTS: 'first-look-modal-step-2',
  COMPLETE: 'first-look-modal-complete',
}

const { track, eventNames, standardTracking } = newAnalytics

/**
 * Initializes our analytics library and tracks the initial page load.
 */
function initialize(
  propertyContext: Record<string, unknown> = {},
  user = {} as User,
  featureFlags = {} as FeatureFlags
): void {
  const googleAnalyticsPath = window.location.pathname

  const extendedFeatureFlags = featureFlags as TesteableFeatureFlags

  // Setting inital GTM Values
  const dataObject = {
    auctionId: propertyContext['auction.id'],
    statusLabel: propertyContext.statusLabel,
    estCreditBid: propertyContext.estCreditBid || '',
    reserve: propertyContext.reserve,
    propertyType: propertyContext['property.type'],
    venueId: propertyContext.venueId,
    equityNumber: propertyContext['property.equityNumber'],
    // @ts-ignore next
    aiAssistant: featureFlags['fe.resi.ai-assistant']?.enabled
      ? 'enabled'
      : 'disabled',
    ...(propertyContext.listing || {}),
    marketingTags:
      Array.isArray(propertyContext.marketingTags) &&
      propertyContext.marketingTags.length > 0
        ? propertyContext.marketingTags
        : null,
    ...(extendedFeatureFlags['fe.resi.ab.testing.onboarding_drawer'] &&
      extendedFeatureFlags['fe.resi.ab.testing.onboarding_drawer']?.test),
    ...(extendedFeatureFlags['fe.resi.ab.testing.signup-propositions'] &&
      extendedFeatureFlags['fe.resi.ab.testing.signup-propositions']?.test),
    ...(extendedFeatureFlags['fe.resi.ab.testing.pdp.save.icon'] &&
      extendedFeatureFlags['fe.resi.ab.testing.pdp.save.icon']?.test),
    ...(extendedFeatureFlags['fe.resi.ab.testing.onboarding_drawer_content'] &&
      extendedFeatureFlags['fe.resi.ab.testing.onboarding_drawer_content']
        ?.test),
    ...(extendedFeatureFlags['fe.resi.enabled-photo-enhancement'] &&
      extendedFeatureFlags['fe.resi.enabled-photo-enhancement']?.test),
    ...(extendedFeatureFlags['fe.resi.ab.testing.signup.thank-you-page'] &&
      extendedFeatureFlags['fe.resi.ab.testing.signup.thank-you-page']?.test),
    testId1: '10010',
    testVariant1: featureFlags['fe.resi.enable-new-navigation']
      ? 'new_nav_feb26'
      : 'control_nav',
  }
  if (user) {
    newAnalytics.trackDataLayerLoaded(user, dataObject)
  }

  newAnalytics
    .init(configment.get())
    .then(() => {
      everyCall = {
        channel: CHANNEL,
        ...propertyContext,
        ...pick(dataObject, [
          'productType',
          'morpheusListingId',
          'morpheusPropertyId',
        ]),
      }

      newAnalytics.firePageTrackingWithContext(
        googleAnalyticsPath,
        SHOULD_FIRE_PAGE_GA_TRACKING
      )
    })
    .catch(logger.error)
}

analytics.track = ({
  eventName = '',
  additionalFields = {},
}: TrackingEvent): void => {
  track(eventName, {
    ...additionalFields,
  })
}

analytics.bidDeposit = {
  track: ({ eventName = '', additionalFields = {} }: BidDepositEvent): void => {
    track(eventName, {
      type: BID_DEPOSIT_EVENT_NAME,
      event_category: BID_DEPOSIT_EVENT_NAME,
      ...additionalFields,
    })
  },
}

/**
 * Popup
 */
analytics.popup = (
  tooltipName: string,
  additionalFields: Record<string, unknown> = {}
): void => {
  track(newAnalytics.eventNames.hoverEvent, {
    tooltipName,
    ...additionalFields,
  })
}

/**
 * Documents
 */
analytics.documents = (doc?: { label: string }): void => {
  // Google Tag Manager dataLayer
  doc &&
    track(newAnalytics.eventNames.download, {
      downloadText: doc.label,
      ...commonTrackingValues(),
    })
}

analytics.compInteraction = (compAddress?: string): void => {
  compAddress &&
    track(newAnalytics.eventNames.localCompsMapClick, {
      compAddress,
    })
}

/**
 * Property
 */
analytics.property = {
  save: (listing = {} as Listing): void => {
    newAnalytics.trackPropertySave(listing, 'save')
  },
  saveSimilarProperty: (listing = {} as Listing, save = true): void => {
    track(newAnalytics.eventNames.similarPropertySave, {
      status: 'success',
      action: save ? 'save' : 'remove',
      ...newAnalytics.standardTracking.listing(listing),
      propertyAddress: Address.toLines(listing)[0],
    })
  },
  trackSimilarPropertyClick: (
    listing: Listing,
    additionalPropertyData: Record<string, unknown> = {}
  ): void => {
    track(newAnalytics.eventNames.similarPropertiesClick, {
      ...additionalPropertyData,
      ...newAnalytics.standardTracking.listing(listing),
    })
  },
  removeSaved: (
    listing: Listing,
    additionalPropertyData?: Record<string, unknown>
  ): void => {
    newAnalytics.trackPropertySave(listing, 'remove', additionalPropertyData)
  },
}

analytics.bidBanner = {
  // For easier reporting, bidStatus is consistently updated, even when the user is logged out.
  unauthenticatedStatusChange: (): void => {
    track(newAnalytics.eventNames.bidStatusChange, {
      bidStatus: 'unauthenticated',
    })
  },
  bidStatusChange: (bidStatus: string): void => {
    track(newAnalytics.eventNames.bidStatusChange, {
      bidStatus,
    })
  },
  confirmBid: {
    success: (bidAmount: number): void => {
      track(newAnalytics.eventNames.confirmBid, {
        confirmationStatus: 'success',
        ...commonTrackingValues(),
        ...{ bidAmount },
      })
    },
    fail: (): void => {
      track(newAnalytics.eventNames.confirmBid, {
        confirmationStatus: 'fail',
        ...commonTrackingValues(),
      })
    },
  },
  nextBidSubmit: (listing: Listing): void => {
    track(newAnalytics.eventNames.pdpYourNextBidSubmit, {
      ...commonTrackingValues(),
      auctionStatus: listing.listing_status,
    })
  },
}

analytics.maxBid = {
  trackSetAutoBidClick: (listing: Listing): void =>
    track(newAnalytics.eventNames.pdpSetAutoBidClick, {
      ...commonTrackingValues(),
      auctionStatus: listing.listing_status,
    }),
  trackSetAutoBidConfirm: {
    success: (listing: Listing, maxBidAmount: number | undefined): void => {
      track(newAnalytics.eventNames.pdpSetAutoBidConfirm, {
        confirmationStatus: 'success',
        ...commonTrackingValues(),
        auctionStatus: listing.listing_status,
        ...{
          maxBid: maxBidAmount,
        },
      })
    },
    fail: (listing: Listing): void => {
      track(newAnalytics.eventNames.pdpSetAutoBidConfirm, {
        confirmationStatus: 'fail',
        ...commonTrackingValues(),
        auctionStatus: listing.listing_status,
      })
    },
  },
  trackIncreaseAutoBidClick: (listing: Listing): void =>
    track(newAnalytics.eventNames.pdpIncreaseAutoBidClick, {
      ...commonTrackingValues(),
      auctionStatus: listing.listing_status,
    }),
  trackIncreaseAutoBidConfirm: {
    success: (listing: Listing, maxBidAmount: number | undefined): void => {
      track(newAnalytics.eventNames.pdpIncreaseAutoBidConfirm, {
        confirmationStatus: 'success',
        ...commonTrackingValues(),
        auctionStatus: listing.listing_status,
        ...{
          maxBid: maxBidAmount,
        },
      })
    },
    fail: (listing: Listing): void => {
      track(newAnalytics.eventNames.pdpIncreaseAutoBidConfirm, {
        confirmationStatus: 'fail',
        ...commonTrackingValues(),
        auctionStatus: listing.listing_status,
      })
    },
  },
  trackAdjustAutoBidClick: (listing: Listing): void =>
    track(newAnalytics.eventNames.pdpAdjustAutoBidClick, {
      ...commonTrackingValues(),
      auctionStatus: listing.listing_status,
    }),
  trackAdjustAutoBidConfirm: {
    success: (listing: Listing, maxBidAmount: number | undefined): void => {
      track(newAnalytics.eventNames.pdpAdjustAutoBidConfirm, {
        confirmationStatus: 'success',
        ...commonTrackingValues(),
        auctionStatus: listing.listing_status,
        maxBid: maxBidAmount,
      })
    },
    fail: (listing: Listing): void => {
      track(newAnalytics.eventNames.pdpAdjustAutoBidConfirm, {
        confirmationStatus: 'fail',
        ...commonTrackingValues(),
        auctionStatus: listing.listing_status,
      })
    },
  },
  trackRemoveAutoBidClick: (listing: Listing): void =>
    track(newAnalytics.eventNames.pdpRemoveAutoBidClick, {
      ...commonTrackingValues(),
      auctionStatus: listing.listing_status,
    }),
  trackRemoveAutoBidConfirm: {
    success: (listing: Listing): void => {
      track(newAnalytics.eventNames.pdpRemoveAutoBidConfirm, {
        confirmationStatus: 'success',
        ...commonTrackingValues(),
        auctionStatus: listing.listing_status,
      })
    },
    fail: (listing: Listing): void => {
      track(newAnalytics.eventNames.pdpRemoveAutoBidConfirm, {
        confirmationStatus: 'fail',
        ...commonTrackingValues(),
        auctionStatus: listing.listing_status,
      })
    },
  },
}

analytics.purchaseTitleReport = (): void => {
  track(newAnalytics.eventNames.purchaseTitleReport, {
    ...commonTrackingValues(),
  })
}

analytics.aiAssistantChat = {
  trackSubmitType: (chatSubmitType: AiChatSubmitType): void => {
    track(newAnalytics.eventNames.pdpAiAssistantPromptSubmit, {
      chatSubmitType,
      ...commonTrackingValues(),
    })
  },
  trackConversationStart: (conversationId: string): void => {
    track(newAnalytics.eventNames.pdpAiConversationStart, {
      conversationId,
      ...commonTrackingValues(),
    })
  },
  trackAiQuestionClick: (linkPosition: number, linkName: string) => {
    // TODO: move event name in eventNames constant in @adc/analytics
    track('pdp-ai-question-click', { linkPosition, linkName })
  },
  trackLiveAgentBannerDisplayed: () => {
    track(newAnalytics.eventNames.liveAgentBannerDisplayed, {
      ...commonTrackingValues(),
    })
  },
  trackLiveAgentClick: () => {
    track(newAnalytics.eventNames.liveAgentBannerClicked, {
      ...commonTrackingValues(),
    })
  },
}

/**
 * TPS trackings
 *
 * NOTE: Was this copied over from tps-pdp and never changed? So that we have multple differnt ways to track the same
 * property data between product type variants? Man this is poop. Needs some rework here.
 */
analytics.trackPDP = ({ featureFlags, listing, user = {} as User }): void => {
  const googleAnalyticsPath = window.location.pathname

  const testeableFeatureFlags = featureFlags as TesteableFeatureFlags

  // Setting initial GTM Values
  // eslint-disable-next-line
  user &&
    newAnalytics.trackDataLayerLoaded(user, {
      auctionId: get(listing, 'event.event_id', ''),
      statusLabel: get(listing, 'listing_status_label', ''),
      estCreditBid: get(listing, 'reserve_price', ''),
      propertyType: get(
        listing,
        'primary_property.summary.structure_type_code'
      ),
      equityNumber: get(listing, 'potential_return_score', 0),
      venueId: get(listing, 'venue.venue_id', ''),
      marketingTags: (() => {
        const tags = (get(listing, 'marketing_tags', []) || [])
          .map((tag: any) => tag?.tag)
          .filter(Boolean)
        return tags.length > 0 ? tags : null
      })(),
      ...newAnalytics.standardTracking.listing(listing),
      ...(testeableFeatureFlags['fe.resi.ab.testing.onboarding_drawer'] &&
        testeableFeatureFlags['fe.resi.ab.testing.onboarding_drawer']?.test),
      ...(testeableFeatureFlags['fe.resi.ab.testing.signup-propositions'] &&
        testeableFeatureFlags['fe.resi.ab.testing.signup-propositions']?.test),
      ...(testeableFeatureFlags['fe.resi.ab.testing.pdp.save.icon'] &&
        testeableFeatureFlags['fe.resi.ab.testing.pdp.save.icon']?.test),
      ...(testeableFeatureFlags[
        'fe.resi.ab.testing.onboarding_drawer_content'
      ] &&
        testeableFeatureFlags['fe.resi.ab.testing.onboarding_drawer_content']
          ?.test),
      ...(testeableFeatureFlags['fe.resi.enabled-photo-enhancement'] &&
        testeableFeatureFlags['fe.resi.enabled-photo-enhancement']?.test),
      ...(testeableFeatureFlags['fe.resi.ab.testing.signup.thank-you-page'] &&
        testeableFeatureFlags['fe.resi.ab.testing.signup.thank-you-page']
          ?.test),
      testId1: '10010',
      testVariant1: featureFlags?.['fe.resi.enable-new-navigation']
        ? 'new_nav_feb26'
        : 'control_nav',
    })

  newAnalytics
    .init(configment.get())
    .then(() => {
      const summary = get(listing, 'primary_property.summary', {})
      const address = get(summary, 'address', {})

      everyCall = {
        channel: CHANNEL,
        'property.type': get(
          listing,
          'primary_property.summary.structure_type_code',
          ''
        ),
        'property.city': get(address, 'municipality', ''),
        'property.county': get(address, 'country_secondary_subdivision', ''),
        'property.state': get(address, 'country_primary_subdivision', ''),
        'property.address': Address.format(summary),
        'property.occupancyStatus': get(
          listing,
          'listing_configuration.occupancy_status',
          ''
        ),
        lob: get(listing, 'listing_configuration.product_type', ''),
        startingBid: get(listing, 'auction.starting_bid', 0),
        'event.id': get(listing, 'event.event_code', ''),
        'auction.id': get(listing, 'event.event_id,', '0'),
        'venue.id': get(listing, 'venue.venue_id', ''),
        'venue.type': get(listing, 'venue.venue_type', ''),
        'asset.type': get(listing, 'listing_configuration.asset_type', ''),
        'auction.date': get(listing, 'auction.auction_date', ''),
        status: get(listing, 'listing_status_label', '').replace(/ /g, ''),
        ...newAnalytics.standardTracking.listing(listing),
      }

      newAnalytics.firePageTrackingWithContext(
        googleAnalyticsPath,
        SHOULD_FIRE_PAGE_GA_TRACKING
      )
    })
    .catch(logger.error)
}

analytics.trackPrint = (data: Listing): void =>
  track(
    newAnalytics.eventNames.print,
    newAnalytics.standardTracking.listing(data)
  )

analytics.trackMakeYourOfferClick = (): void =>
  track(newAnalytics.eventNames.makeYourOfferPDP)

/**
 * First Look Modal
 */
analytics.firstLookModal = {
  start: (): void => {
    track(newAnalytics.eventNames.firstLookModal.start)
  },
  acknowledgements: (buyerCertificationType: string): void => {
    track(newAnalytics.eventNames.firstLookModal.step2, {
      buyerCertificationType,
    })
  },
  back: (buyerCertificationType: string): void => {
    track(newAnalytics.eventNames.firstLookModal.back, {
      buyerCertificationType,
    })
  },
  done: (buyerCertificationType: string): void => {
    track(newAnalytics.eventNames.firstLookModal.done, {
      buyerCertificationType,
    })
  },
  complete: (buyerCertificationType: string): void => {
    track(newAnalytics.eventNames.firstLookModal.complete, {
      buyerCertificationType,
    })
  },
}

analytics.remoteBidding = {
  qualifyRemoteBidButtonClick: (isUserLoggedIn: boolean): void =>
    track(newAnalytics.eventNames.qualifyRemoteBidButton, {
      ...commonTrackingValues(),
      userLoggedIn: isUserLoggedIn,
    }),
  joinTheAuctionRemoteBidButtonClick: (isUserLoggedIn: boolean): void =>
    track('pdp-join-auction-now-click', {
      ...commonTrackingValues(),
      userLoggedIn: isUserLoggedIn,
    }),
}

analytics.privateSeller = {
  makeAnOfferBidBoxButtonClick: (): void =>
    track('make-an-offer-bid-box', {
      ...commonTrackingValues(),
    }),
  makeAnOfferStickyHeaderButtonClick: (): void =>
    track('make-an-offer-sticky-header', {
      ...commonTrackingValues(),
    }),
  makeAnOffer: ({
    intent,
    status,
    listing,
  }: {
    intent: string
    status: string
    listing: Listing
  }): void =>
    analytics.track({
      eventName:
        intent === 'submit' ? 'make-an-offer-submit' : 'make-an-offer-update',
      additionalFields: {
        ...standardTracking.listing(listing),
        status,
      },
    }),
}

analytics.dualTrack = {
  pathSelected: ({
    path,
    source,
  }: {
    path: string
    source: 'toggle' | 'modal'
  }): void =>
    track(DUAL_TRACK_PATH_SELECTED_EVENT_NAME, {
      path,
      source,
      ...commonTrackingValues(),
    }),
  modalViewed: (): void =>
    track(DUAL_TRACK_MODAL_VIEWED_EVENT_NAME, {
      ...commonTrackingValues(),
    }),
  modalOutsideClick: (): void =>
    track(DUAL_TRACK_MODAL_OUTSIDE_CLICK_EVENT_NAME, {
      ...commonTrackingValues(),
    }),
}
analytics.trackSearchSubmit = (user: User): void => {
  track(PDP_SEARCH_SUBMIT_EVENT_NAME, {
    userLoggedIn: user.logged_in,
    cookieId: Cookies.get('BRID'),
    ...standardTracking.user(user),
    searchBoxSelection: 'typed',
  })
}

analytics.initialize = initialize

export default analytics
export {
  ACTION_VALUES,
  REO_PDP_PAGE_NAME,
  FIRST_LOOK_MODAL,
  eventNames,
  standardTracking,
}
